import { Link } from 'react-router-dom';
import { useId, useState, type ReactNode } from 'react';
import { Check, ChevronDown, ChevronRight, ClipboardList, FlaskConical, Repeat, SkipForward, X } from 'lucide-react';
import { isDosingPlanTask, type Task } from '../../types/task';
import type { AiSuggestion } from '../../types/aiSuggestion';
import { useTranslation } from '../../i18n/useTranslation';
import { Heading } from '../atoms/Typography/Heading';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { EmptyState } from '../atoms/EmptyState/EmptyState';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { useTaskFormat, type TaskFormat } from './taskFormat';
import './TaskList.css';

function priorityTone(priority: string): BadgeTone {
  switch (priority) {
    case 'critical':
    case 'high': return 'danger';
    case 'medium': return 'warning';
    default: return 'neutral';
  }
}

function TaskMeta({ task, fmt, showDone = false }: { task: Task; fmt: TaskFormat; showDone?: boolean }) {
  const { t } = useTranslation();
  const due = fmt.due(task);
  const recurring = task.isRecurring ?? Boolean(task.recurrenceRule);
  const fromDosingPlan = isDosingPlanTask(task);
  const dose = fromDosingPlan ? fmt.dose(task) : null;
  return (
    <span className="task-list__row-meta">
      {due && <time dateTime={task.startsAt ?? task.dueDate ?? undefined}>{due}</time>}
      {dose && <span data-testid={`task-dose-${task.id}`}>{dose}</span>}
      {fromDosingPlan ? (
        <span className="task-list__recurring">
          <FlaskConical size={14} aria-hidden="true" />
          {t('tasks.dosing.badge')}
        </span>
      ) : task.category && <span>{fmt.category(task.category)}</span>}
      {recurring && (
        <span className="task-list__recurring">
          <Repeat size={14} aria-hidden="true" />
          {t('tasks.recurrence.badge')}
        </span>
      )}
      {showDone && <span>{fmt.status(task.status)}</span>}
    </span>
  );
}

/** "From voice note" / "From journal entry" and a link to that entry (UC-B: the task links to its entry). */
function TaskSourceLink({ task, entryId }: { task: Task; entryId: string }) {
  const { t } = useTranslation();
  return (
    <span className="task-list__source">
      <Badge tone="info">{task.source === 'voice' ? t('voice.tasks.fromVoice') : t('voice.tasks.fromJournal')}</Badge>
      <Link
        className="task-list__source-link"
        to={`/aquariums/${encodeURIComponent(task.aquariumId)}/journal/${encodeURIComponent(entryId)}`}
        aria-label={t('voice.tasks.openEntryLabel', { title: task.title })}
      >
        {t('voice.tasks.openEntry')}
      </Link>
    </span>
  );
}

interface TaskRowProps {
  task: Task;
  fmt: TaskFormat;
  onComplete: (taskId: string, notes?: string) => void;
  onSkip: (taskId: string) => void;
  onOpen: (task: Task) => void;
}

function TaskRow({ task, fmt, onComplete, onSkip, onOpen }: TaskRowProps) {
  const { t } = useTranslation();
  return (
    <li className="task-list__row" data-testid={`task-row-${task.id}`}>
      <Button
        variant="ghost"
        className="task-list__row-body"
        onClick={() => onOpen(task)}
        aria-label={t('tasks.list.openTask', { title: task.title })}
      >
        <span className="task-list__row-title">{task.title}</span>
        <TaskMeta task={task} fmt={fmt} />
      </Button>
      <Badge tone={priorityTone(task.priority)} className="task-list__priority">{fmt.priority(task.priority)}</Badge>
      {task.sourceJournalEntryId && <TaskSourceLink task={task} entryId={task.sourceJournalEntryId} />}
      <div className="task-list__row-actions">
        <Button
          variant="ghost"
          size="sm"
          className="task-list__action-btn"
          onClick={() => onComplete(task.id, '')}
          aria-label={t('tasks.list.completeTask', { title: task.title })}
          data-testid={`complete-btn-${task.id}`}
        >
          <Check size={16} aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="task-list__action-btn"
          onClick={() => onSkip(task.id)}
          aria-label={t('tasks.list.skipTask', { title: task.title })}
          data-testid={`skip-btn-${task.id}`}
        >
          <SkipForward size={16} aria-hidden="true" />
        </Button>
      </div>
    </li>
  );
}

interface AiSuggestionRowProps {
  suggestion: AiSuggestion;
  fmt: TaskFormat;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
  onOpen: (suggestion: AiSuggestion) => void;
}

function AiSuggestionRow({ suggestion, fmt, onAccept, onDismiss, onOpen }: AiSuggestionRowProps) {
  const { t } = useTranslation();
  return (
    <li className="task-list__row" data-testid={`suggestion-row-${suggestion.id}`}>
      <Button
        variant="ghost"
        className="task-list__row-body"
        onClick={() => onOpen(suggestion)}
        aria-label={t('tasks.list.openSuggestion', { title: suggestion.title })}
      >
        <span className="task-list__row-title">{suggestion.title}</span>
        {suggestion.category && <span className="task-list__row-meta">{fmt.category(suggestion.category)}</span>}
      </Button>
      <Badge tone="ai">{t('tasks.list.aiSuggestion')}</Badge>
      <div className="task-list__row-actions">
        <Button
          variant="ghost"
          size="sm"
          className="task-list__action-btn"
          onClick={() => onAccept(suggestion.id)}
          aria-label={t('tasks.list.acceptSuggestion', { title: suggestion.title })}
          data-testid={`accept-btn-${suggestion.id}`}
        >
          <Check size={16} aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="task-list__action-btn"
          onClick={() => onDismiss(suggestion.id)}
          aria-label={t('tasks.list.dismissSuggestion', { title: suggestion.title })}
          data-testid={`dismiss-btn-${suggestion.id}`}
        >
          <X size={16} aria-hidden="true" />
        </Button>
      </div>
    </li>
  );
}

interface SectionProps {
  title: string;
  count: number;
  children: ReactNode;
  variant: 'overdue' | 'today' | 'upcoming' | 'ai' | 'completed';
}

function Section({ title, count, children, variant }: SectionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const bodyId = useId();
  const label = t('tasks.list.sectionCount', { title, count });

  return (
    <section className={`task-list__section task-list__section--${variant}`} aria-label={label}>
      <div className="task-list__section-header" data-testid={`section-header-${variant}`}>
        <Heading level={3} className="task-list__section-title">{label}</Heading>
        <Button
          variant="ghost"
          size="sm"
          className="task-list__section-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={t('tasks.list.toggleSection', { title })}
        >
          {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        </Button>
      </div>
      {open && <ul id={bodyId} className="task-list__section-body">{children}</ul>}
    </section>
  );
}

export interface TaskListProps {
  aquariumId: string;
  overdueTasks: Task[];
  todayTasks: Task[];
  upcomingTasks: Task[];
  completedTasks: Task[];
  aiSuggestions: AiSuggestion[];
  isLoading: boolean;
  /** The list could not be loaded. */
  isError: boolean;
  onRetry?: () => void;
  onCompleteTask: (taskId: string, notes?: string) => void;
  onSkipTask: (taskId: string) => void;
  onOpenTask: (task: Task) => void;
  onCreateTask: () => void;
  onAcceptSuggestion: (id: string) => void;
  onDismissSuggestion: (id: string) => void;
  onOpenSuggestion: (suggestion: AiSuggestion) => void;
}

export function TaskList({
  overdueTasks,
  todayTasks,
  upcomingTasks,
  completedTasks,
  aiSuggestions,
  isLoading,
  isError,
  onRetry,
  onCompleteTask,
  onSkipTask,
  onOpenTask,
  onCreateTask,
  onAcceptSuggestion,
  onDismissSuggestion,
  onOpenSuggestion,
}: TaskListProps) {
  const { t } = useTranslation();
  const fmt = useTaskFormat();

  if (isLoading) {
    return (
      <LiveRegion busy className="task-list__loading" data-testid="task-list-loading">
        {t('tasks.list.loading')}
      </LiveRegion>
    );
  }

  if (isError) {
    return (
      <div className="task-list" data-testid="task-list-error">
        <LoadErrorState title={t('tasks.list.loadError')} onRetry={onRetry} />
      </div>
    );
  }

  const totalVisible =
    overdueTasks.length + todayTasks.length + upcomingTasks.length + aiSuggestions.length + completedTasks.length;

  if (totalVisible === 0) {
    return (
      <div className="task-list" data-testid="task-list">
        <EmptyState
          className="task-list__empty"
          icon={<ClipboardList size={40} />}
          headline={t('tasks.list.emptyHeadline')}
          description={t('tasks.list.emptyDescription')}
          actionLabel={t('tasks.list.emptyAction')}
          actionVariant="secondary"
          onAction={onCreateTask}
        />
      </div>
    );
  }

  const rows = (tasks: Task[]) => tasks.map((task) => (
    <TaskRow key={task.id} task={task} fmt={fmt} onComplete={onCompleteTask} onSkip={onSkipTask} onOpen={onOpenTask} />
  ));

  return (
    <div className="task-list" data-testid="task-list">
      {overdueTasks.length > 0 && (
        <Section title={t('tasks.list.overdue')} count={overdueTasks.length} variant="overdue">{rows(overdueTasks)}</Section>
      )}
      {todayTasks.length > 0 && (
        <Section title={t('tasks.list.today')} count={todayTasks.length} variant="today">{rows(todayTasks)}</Section>
      )}
      {upcomingTasks.length > 0 && (
        <Section title={t('tasks.list.upcoming')} count={upcomingTasks.length} variant="upcoming">{rows(upcomingTasks)}</Section>
      )}
      {aiSuggestions.length > 0 && (
        <Section title={t('tasks.list.suggestions')} count={aiSuggestions.length} variant="ai">
          {aiSuggestions.map((suggestion) => (
            <AiSuggestionRow
              key={suggestion.id}
              suggestion={suggestion}
              fmt={fmt}
              onAccept={onAcceptSuggestion}
              onDismiss={onDismissSuggestion}
              onOpen={onOpenSuggestion}
            />
          ))}
        </Section>
      )}
      {completedTasks.length > 0 && (
        <Section title={t('tasks.list.done')} count={completedTasks.length} variant="completed">
          {completedTasks.map((task) => (
            <li key={task.id} className="task-list__row task-list__row--completed" data-testid={`completed-row-${task.id}`}>
              <Button
                variant="ghost"
                className="task-list__row-body"
                onClick={() => onOpenTask(task)}
                aria-label={t('tasks.list.openTask', { title: task.title })}
              >
                <span className="task-list__row-title task-list__row-title--done">{task.title}</span>
                <TaskMeta task={task} fmt={fmt} showDone />
              </Button>
            </li>
          ))}
        </Section>
      )}
    </div>
  );
}

export default TaskList;
